import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, MessageSquare, ShieldCheck, User, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import AdminLayout from './AdminLayout';
import Breadcrumb from '../../components/common/Breadcrumb';
import AdminReviewFormDrawer from '../../components/admin/AdminReviewFormDrawer';
import Toast from '../../components/common/Toast';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://vmc-backend-vt2w.onrender.com/api';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
});

/* ── Helpers ── */
const StarRow = ({ value, size = 13 }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        size={size}
        className={i <= Math.round(value) ? 'fill-amber-400 text-amber-400' : 'fill-slate-100 text-slate-300'}
      />
    ))}
  </span>
);

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const truncate = (str, max = 100) => {
  if (!str) return '—';
  return str.length > max ? str.slice(0, max) + '…' : str;
};

/* ── Source badge ── */
const SourceBadge = ({ review }) => {
  if (review.added_by_admin) {
    const adminName = review.admin_user?.name || 'Admin';
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-200 rounded-full text-[10px] font-semibold text-amber-700 whitespace-nowrap">
        <ShieldCheck size={10} className="text-amber-500" />
        Admin · {adminName}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 rounded-full text-[10px] font-medium text-slate-500 whitespace-nowrap">
      <User size={10} />
      Direct
    </span>
  );
};

/* ── Skeleton row ── */
const SkeletonRow = () => (
  <tr className="border-b border-slate-100">
    {[1, 2, 3, 4, 5, 6].map((i) => (
      <td key={i} className="px-4 py-3">
        <div className="h-3.5 bg-slate-200 rounded animate-pulse" />
      </td>
    ))}
  </tr>
);

/* ══════════════════════════════════════════ */

const AdminBusinessReviewsPage = () => {
  const { id: businessId } = useParams();
  const navigate = useNavigate();

  const [business, setBusiness] = useState(null);
  const [businessLoading, setBusinessLoading] = useState(true);

  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [pagination, setPagination] = useState({ limit: 20, offset: 0, total: 0, hasMore: false });

  // Filters & sort
  const [ratingFilter, setRatingFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all'); // all | admin | direct
  const [sortBy, setSortBy] = useState('recent');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('add'); // 'add' | 'view'
  const [selectedReview, setSelectedReview] = useState(null);

  // Toast
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  /* ── Fetch business info ── */
  useEffect(() => {
    const fetchBusiness = async () => {
      try {
        setBusinessLoading(true);
        const res = await fetch(`${API_BASE}/admin/businesses/${businessId}`, { headers: authHeaders() });
        if (!res.ok) throw new Error('Not found');
        const data = await res.json();
        setBusiness(data.data);
      } catch {
        showToast('Could not load business details', 'error');
      } finally {
        setBusinessLoading(false);
      }
    };
    fetchBusiness();
  }, [businessId]);

  /* ── Fetch reviews ── */
  const fetchReviews = useCallback(async () => {
    setReviewsLoading(true);
    try {
      const params = new URLSearchParams({
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
        sort_by: sortBy,
      });
      if (ratingFilter !== 'all') params.append('rating', ratingFilter);
      if (sourceFilter === 'admin') params.append('added_by_admin', 'true');
      if (sourceFilter === 'direct') params.append('added_by_admin', 'false');

      const res = await fetch(
        `${API_BASE}/admin/businesses/${businessId}/reviews?${params}`,
        { headers: authHeaders() }
      );
      if (!res.ok) throw new Error('Failed to load reviews');
      const data = await res.json();
      setReviews(data.data || []);
      setPagination(data.pagination || { limit: itemsPerPage, offset: 0, total: 0, hasMore: false });
    } catch {
      showToast('Failed to load reviews', 'error');
    } finally {
      setReviewsLoading(false);
    }
  }, [businessId, currentPage, ratingFilter, sourceFilter, sortBy]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [ratingFilter, sourceFilter, sortBy]);

  /* ── Drawer handlers ── */
  const openAddReviewDrawer = () => {
    setDrawerMode('add');
    setSelectedReview(null);
    setDrawerOpen(true);
  };

  const openViewReviewDrawer = (review) => {
    setDrawerMode('view');
    setSelectedReview(review);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedReview(null);
    setDrawerMode('add');
  };

  /* ── Stats ── */
  const adminCount = reviews.filter((r) => r.added_by_admin).length;
  const directCount = reviews.filter((r) => !r.added_by_admin).length;
  const avgRating = reviews.length > 0
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  const breadcrumbItems = [
    { label: 'Admin', href: '/admin/dashboard' },
    { label: 'Businesses', href: '/admin/businesses' },
    { label: businessLoading ? '…' : (business?.business_name || businessId), href: `/admin/businesses` },
    { label: 'Reviews' },
  ];

  return (
    <AdminLayout title="Business Reviews">
      <div className="space-y-5">

        {/* ── Breadcrumb & Header ── */}
        <div>
          <Breadcrumb items={breadcrumbItems} />
          <div className="flex items-start justify-between gap-4 mt-1">
            <div>
              <h1 className="text-[22px] font-bold text-slate-900 leading-tight">
                {businessLoading ? (
                  <span className="inline-block h-6 w-56 bg-slate-200 rounded animate-pulse" />
                ) : (
                  business?.business_name || 'Business Reviews'
                )}
              </h1>
              <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                {business?.avg_rating ? (
                  <span className="inline-flex items-center gap-1.5">
                    <StarRow value={business.avg_rating} size={14} />
                    <span className="text-[12px] font-semibold text-slate-700">{parseFloat(business.avg_rating).toFixed(1)}</span>
                    <span className="text-[12px] text-slate-400">avg</span>
                  </span>
                ) : null}
                <span className="text-[12px] text-slate-400">
                  {pagination.total} review{pagination.total !== 1 ? 's' : ''} total
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <ChevronLeft size={15} />
                Back
              </button>
              <button
                onClick={openAddReviewDrawer}
                className="inline-flex items-center gap-2 px-4 py-2 text-[12px] font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus size={15} />
                Add Review
              </button>
            </div>
          </div>
        </div>

        {/* ── Stats Bar ── */}
        <div className="grid grid-cols-4 gap-3">
          <div className="p-3.5 bg-white border border-slate-200 rounded-lg">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Total Reviews</p>
            <p className="text-[22px] font-bold text-slate-900 mt-1">{pagination.total}</p>
          </div>
          <div className="p-3.5 bg-white border border-slate-200 rounded-lg">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Avg Rating</p>
            <p className="text-[22px] font-bold text-amber-600 mt-1">
              {business?.avg_rating ? parseFloat(business.avg_rating).toFixed(1) : '—'}
            </p>
          </div>
          <div className="p-3.5 bg-white border border-slate-200 rounded-lg">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Admin-Submitted</p>
            <p className="text-[22px] font-bold text-amber-700 mt-1">
              {reviewsLoading ? '—' : adminCount}
            </p>
          </div>
          <div className="p-3.5 bg-white border border-slate-200 rounded-lg">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Direct Reviews</p>
            <p className="text-[22px] font-bold text-slate-700 mt-1">
              {reviewsLoading ? '—' : directCount}
            </p>
          </div>
        </div>

        {/* ── Toolbar ── */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Rating filter */}
          <select
            value={ratingFilter}
            onChange={(e) => setRatingFilter(e.target.value)}
            className="px-3 py-1.5 text-[12px] border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 bg-white"
          >
            <option value="all">Rating: All</option>
            {[5, 4, 3, 2, 1].map((r) => (
              <option key={r} value={r}>{r} Star{r !== 1 ? 's' : ''}</option>
            ))}
          </select>

          {/* Source filter */}
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="px-3 py-1.5 text-[12px] border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 bg-white"
          >
            <option value="all">Source: All</option>
            <option value="admin">Admin-submitted</option>
            <option value="direct">Direct</option>
          </select>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-1.5 text-[12px] border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 bg-white"
          >
            <option value="recent">Sort: Newest first</option>
            <option value="rating_high">Sort: Rating high–low</option>
            <option value="rating_low">Sort: Rating low–high</option>
          </select>

          <span className="ml-auto text-[11px] text-slate-400">
            {pagination.total} result{pagination.total !== 1 ? 's' : ''}
          </span>
        </div>

        {/* ── Table ── */}
        <div className="overflow-hidden border border-slate-200 rounded-lg bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[780px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide w-[180px]">Reviewer</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide w-[90px]">Rating</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide w-[170px]">Title</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Content</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide w-[100px]">Date</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide w-[130px]">Source</th>
                </tr>
              </thead>
              <tbody>
                {reviewsLoading ? (
                  Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
                ) : reviews.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-14 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <MessageSquare size={32} className="text-slate-300" />
                        <p className="text-[13px] font-medium text-slate-500">No reviews yet</p>
                        <p className="text-[12px] text-slate-400">
                          {ratingFilter !== 'all' || sourceFilter !== 'all'
                            ? 'Try clearing the filters'
                            : 'Click "Add Review" to submit the first one'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  reviews.map((review, idx) => (
                    <tr
                      key={review.id}
                      onClick={() => openViewReviewDrawer(review)}
                      className={`border-b border-slate-100 hover:bg-slate-50/60 transition-colors cursor-pointer ${idx % 2 === 0 ? '' : 'bg-slate-50/30'}`}
                    >
                      {/* Reviewer */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-600 text-[11px] font-bold flex items-center justify-center shrink-0">
                            {review.reviewer?.name?.charAt(0).toUpperCase() || '?'}
                          </div>
                          <div className="min-w-0">
                            <p className="text-[12px] font-medium text-slate-900 truncate">{review.reviewer?.name || 'Unknown'}</p>
                            <p className="text-[10px] text-slate-400 truncate">{review.reviewer?.email || ''}</p>
                          </div>
                        </div>
                      </td>

                      {/* Rating */}
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-0.5">
                          <StarRow value={review.rating} size={12} />
                          <span className="text-[11px] font-semibold text-slate-700">{review.rating}.0</span>
                        </div>
                      </td>

                      {/* Title */}
                      <td className="px-4 py-3">
                        <p className="text-[12px] font-medium text-slate-800 line-clamp-2" title={review.title}>
                          {review.title || '—'}
                        </p>
                      </td>

                      {/* Content preview */}
                      <td className="px-4 py-3">
                        <p className="text-[12px] text-slate-600 line-clamp-2" title={review.content}>
                          {truncate(review.content, 120)}
                        </p>
                      </td>

                      {/* Date */}
                      <td className="px-4 py-3 text-[12px] text-slate-500 whitespace-nowrap">
                        {formatDate(review.created_at)}
                      </td>

                      {/* Source */}
                      <td className="px-4 py-3">
                        <SourceBadge review={review} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Pagination ── */}
        {pagination.total > itemsPerPage && (
          <div className="flex items-center justify-between">
            <span className="text-[12px] text-slate-500">
              Showing {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, pagination.total)} of {pagination.total}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1 || reviewsLoading}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-[12px] border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={13} /> Prev
              </button>
              <span className="text-[12px] text-slate-600 font-medium px-1">
                {currentPage}
              </span>
              <button
                onClick={() => setCurrentPage((p) => p + 1)}
                disabled={!pagination.hasMore || reviewsLoading}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-[12px] border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next <ChevronRight size={13} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Review Form Drawer ── */}
      <AdminReviewFormDrawer
        businessId={businessId}
        businessName={business?.business_name || ''}
        mode={drawerMode}
        reviewData={selectedReview}
        isOpen={drawerOpen}
        onClose={closeDrawer}
        onReviewSubmitted={() => {
          showToast('Review submitted successfully!', 'success');
          fetchReviews();
          closeDrawer();
          // Optionally refresh business info to update avg_rating
          setBusiness((prev) =>
            prev ? { ...prev } : prev
          );
        }}
      />

      {/* ── Toast ── */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </AdminLayout>
  );
};

export default AdminBusinessReviewsPage;
